import React, { useState, useRef, useEffect } from 'react';
import { StripeCardElement } from '@stripe/stripe-js';
import maxBy from 'lodash/maxBy';
import now from 'lodash/now';
// @ts-ignore
import ConfettiGenerator from 'confetti-js';

import { useCurrentUser } from 'hooks/useCurrentUser';
import { SeditionStripeElements, stripePromise } from 'components/payments/SeditionStripeElements';

import * as Types from 'types';

import { BidInput } from './BidInput';
import { CurrentBid } from './CurrentBid';
import { BidModal, BidModalStates } from './BidModal';
import { createPaymentIntent, deletePaymentMethod } from 'features/stripe/api';
import {
  createBid,
  createBidUsingPaymentIntent,
  isErrorNoCard,
  isErrorRequiresAction,
} from 'features/bids/api';

type Props = {
  auction: Types.Auction;
  editionId: number;
  artworkTitle: string;
  disabled?: boolean;
  bids: Types.Bid[] | undefined;
};

export const Auction: React.FC<Props> = ({ editionId, artworkTitle, bids, auction }) => {
  const user = useCurrentUser();
  const clearBidRef = useRef<() => void>();

  const [bidError, setBidError] = useState<undefined | string>();
  const [bidModalOpen, setBidModalOpen] = useState(false);
  const [bidModalState, setBidModalState] = useState<BidModalStates>('loading');
  const [bidModalCCLast4, setBidModalCCLast4] = useState<undefined | string>();
  const [bidPriceCents, setBidPriceCents] = useState<number | undefined>(undefined);

  const maxBid = maxBy(bids, (bid) => bid.priceCents);
  const userBid = user
    ? bids &&
      maxBy(
        bids.filter((bid) => bid.user.id === user.id),
        'priceCents',
      )
    : undefined;

  const closeModal = () => {
    setBidModalOpen(false);
    setBidError('');
    setBidModalState('loading');
    clearBidRef.current && clearBidRef.current();
  };

  // Dry run to see if bid would succeed.
  // If there is no CC we'll get an error.
  const handleBidAttempt = async (priceCents: number) => {
    try {
      setBidModalState('loading');
      setBidPriceCents(priceCents);
      setBidModalOpen(true);
      const result = await createBid(editionId, priceCents, {
        dry: true,
      });
      if (isErrorNoCard(result.error)) {
        setBidModalState('cc-waiting-user');
      } else {
        setBidModalCCLast4(result.paymentMethod?.card.last4);
        setBidModalState('confirm-waiting-user');
      }
    } catch {
      setBidError('Unknown error occurred, please try again');
      setBidModalState('error');
    }
  };

  const handleBidSubmit = async (priceCents: number) => {
    try {
      setBidModalState('confirm-submitting-bid');
      const { bid, error } = await createBid(editionId, priceCents);

      if (bid) {
        setBidModalState('confirm-success');
        clearBidRef.current && clearBidRef.current();
        setTimeout(closeModal, 3000);
      } else if (isErrorNoCard(error)) {
        setBidModalState('cc-waiting-user');
      } else if (isErrorRequiresAction(error)) {
        const stripe = await stripePromise;
        if (stripe) {
          // our client_secret is stored in error_variable
          const stripeResult = await stripe.confirmCardPayment(error.error_variable);
          const { paymentIntent } = stripeResult;
          if (stripeResult.error) {
            setBidModalState('cc-waiting-user');
            setBidError(stripeResult.error.message);
          } else if (paymentIntent) {
            await createBidUsingPaymentIntent(editionId, paymentIntent.id);
            setBidModalState('confirm-success');
            clearBidRef.current && clearBidRef.current();
            setTimeout(closeModal, 3000);
          }
        } else {
          bugsnag.notify('Unable to load Stripe');
          throw 'An unexpected error has occurred. Please refresh the page.';
        }
      } else {
        bugsnag.notify('Unhandled return value');
        throw 'An unexpected error has occurred. Please refresh the page.';
      }
    } catch (error) {
      setBidError('Unknown error occurred, please try again');
      setBidModalState('error');
    }
  };

  const handleCreditCardSubmit = async (cardElement: StripeCardElement) => {
    setBidModalState('cc-submitting');
    setBidError(undefined);
    const stripe = await stripePromise;

    if (!stripe) {
      throw 'Stripe not loaded';
    }

    if (!bidPriceCents) {
      throw 'Price not entered';
    }

    const { data, error } = await createPaymentIntent(bidPriceCents);
    if (error || !data) {
      setBidModalState('cc-waiting-user');
      setBidError('Unknown error occurred, please try again');
      return;
    }

    const result = await stripe.confirmCardPayment(data.client_secret, {
      payment_method: {
        card: cardElement,
      },
      setup_future_usage: 'off_session',
    });

    if (result.error) {
      // Show error to your customer
      setBidModalState('cc-waiting-user');
      setBidError(result.error.message);
    } else {
      const bid = await createBidUsingPaymentIntent(editionId, result.paymentIntent.id);

      if (bid) {
        setBidModalState('cc-success');
        clearBidRef.current && clearBidRef.current();
        setTimeout(closeModal, 3000);

        // if (result.paymentIntent.status === 'succeeded') {
        //   // Show a success message to your customer
        //   // There's a risk of the customer closing the window before callback execution
        //   // Set up a webhook or plugin to listen for the payment_intent.succeeded event
        //   // to save the card to a Customer
        //   // The PaymentMethod ID can be found on result.paymentIntent.payment_method
        // }
      } else {
        setBidModalState('error');
        bugsnag.notify(`Captured, but bid not created ${result.paymentIntent.id}`);
        setBidError('Error while trying to submit a bid.');
      }
    }
  };

  const handleChangeCard = async (last4: string) => {
    setBidModalState('cc-waiting-user');
    setBidModalCCLast4(undefined);
    await deletePaymentMethod(last4);
  };

  const areBidsFetching = bids === undefined;
  const ended = Boolean(auction.endsAt && auction.endsAt < now());

  useEffect(() => {
    if (auction.winningBidId) {
      const endedAgoHours = (now() - (auction.endsAt || 0)) / 1000 / 60 / 60;
      // Show confetti for 24 hours
      if (endedAgoHours > 0 && endedAgoHours < 24) {
        const confettiSettings = {
          target: 'confetti_canvas',
          height: window.innerHeight * 2.5,
          respawn: false,
          max: 280,
        };
        const confetti = new ConfettiGenerator(confettiSettings);
        confetti.render();
      }
    }
  }, [auction.winningBidId]);

  return (
    <SeditionStripeElements auctionMode={true}>
      {areBidsFetching ? null : <CurrentBid maxBid={maxBid} userBid={userBid} auction={auction} />}
      <BidInput
        minCents={maxBid ? maxBid.priceCents + 100 : 100}
        onMount={(clearHandler) => (clearBidRef.current = clearHandler)}
        onSubmit={handleBidAttempt}
        disabled={ended || !user}
      />
      {bidPriceCents ? (
        <BidModal
          state={bidModalState}
          priceCents={bidPriceCents}
          isOpen={bidModalOpen}
          errorMessage={bidError}
          artworkTitle={artworkTitle}
          creditCardLast4={bidModalCCLast4}
          onSubmitCreditCard={handleCreditCardSubmit}
          onSubmitBid={handleBidSubmit}
          onRequestClose={closeModal}
          onRequestChangeCard={handleChangeCard}
        />
      ) : null}
    </SeditionStripeElements>
  );
};
